/**
 * VOL_SPIKE_STRIP — Volume Spike Intensity Strip
 *
 * Every trading day becomes a thin full-height cell in the sub-pane, colour-coded
 * by how far above the n-period volume MA the bar's volume is:
 *
 *   - grey-blue  = at or below MA
 *   - amber      = ~50 % above MA
 *   - orange     = ~150 % above MA
 *   - deep red   = ≥ 300 % above MA
 *
 * Hovering over any cell shows the exact % deviation in the legend/tooltip.
 */

import { IndicatorSeries, LineType, type IndicatorTemplate } from 'klinecharts';

// ── Heat-ramp colour helpers ──────────────────────────────────────────────────

type RGBA = [number, number, number, number];

function lerpRGBA(a: RGBA, b: RGBA, t: number): string {
  const r  = Math.round(a[0] + (b[0] - a[0]) * t);
  const g  = Math.round(a[1] + (b[1] - a[1]) * t);
  const bl = Math.round(a[2] + (b[2] - a[2]) * t);
  const al = +(a[3] + (b[3] - a[3]) * t).toFixed(2);
  return `rgba(${r},${g},${bl},${al})`;
}

// 4-stop gradient: dim blue-grey → amber → orange → deep red
const COLOR_STOPS: [number, RGBA][] = [
  [0,   [80,  80, 110, 0.18]],
  [50,  [255, 200,   0, 0.65]],
  [150, [255, 100,   0, 0.85]],
  [300, [239,  83,  80, 1.00]],
];

function spikeColor(pct: number | null): string {
  if (pct == null || pct <= 0) return 'rgba(80,80,110,0.18)';
  for (let s = 0; s < COLOR_STOPS.length - 1; s++) {
    const [p1, c1] = COLOR_STOPS[s];
    const [p2, c2] = COLOR_STOPS[s + 1];
    if (pct <= p2) {
      const t = Math.max(0, Math.min(1, (pct - p1) / (p2 - p1)));
      return lerpRGBA(c1, c2, t);
    }
  }
  return 'rgba(239,83,80,1.00)'; // ≥ 300 %
}

// ── Internal types ────────────────────────────────────────────────────────────

type KBar = { timestamp: number; volume?: number };

export type SpikeStripRow = { vol: number | null; pct: number | null; ma: number | null };

type DrawParams = {
  ctx:          CanvasRenderingContext2D;
  visibleRange: { from: number; to: number };
  bounding:     { width: number; height: number; left: number; top: number };
  xAxis:        { convertToPixel: (i: number) => number };
  indicator:    { result: SpikeStripRow[] };
  barSpace:     { bar: number; gapBar: number };
};

// ── Indicator template ────────────────────────────────────────────────────────

const volSpikeStrip: IndicatorTemplate<SpikeStripRow> = {
  name:       'VOL_SPIKE_STRIP',
  shortName:  'SpikeStrip',
  series:     IndicatorSeries.Normal,
  shouldOhlc: false,

  // Three hidden figures feed the crosshair legend: vol (raw), ma (MA average),
  // pct (% deviation). All lines have size=0 so draw() handles all canvas output.
  // The first line's colour is user-configurable via the indicator config modal;
  // ChartLegend reads it from indicatorColors['VOL_SPIKE_STRIP'][0].
  figures: [
    { key: 'vol', title: 'Vol: ',  type: 'line' },
    { key: 'ma',  title: 'MA: ',   type: 'line' },
    { key: 'pct', title: '\u0394%: ', type: 'line' },
  ],

  styles: {
    lines: [
      { style: LineType.Solid, size: 0, color: '#60A5FA',     smooth: false, dashedValue: [2, 2] },
      { style: LineType.Solid, size: 0, color: 'transparent', smooth: false, dashedValue: [2, 2] },
      { style: LineType.Solid, size: 0, color: 'transparent', smooth: false, dashedValue: [2, 2] },
    ],
  },

  calc: (dataList, indicator) => {
    const params = (indicator as { calcParams?: number[] }).calcParams;
    const maLen  = Math.max(1, Math.floor(params?.[0] ?? 20));
    const bars   = dataList as unknown as KBar[];

    return bars.map((_, i) => {
      const vol = bars[i].volume;
      if (vol == null || i < maLen - 1) return { vol: null, pct: null, ma: null };

      let sum = 0;
      for (let j = i - maLen + 1; j <= i; j++) {
        const v = bars[j].volume;
        if (v == null) return { vol: null, pct: null, ma: null };
        sum += v;
      }
      const ma  = sum / maLen;
      const pct = ma > 0 ? ((vol - ma) / ma) * 100 : null;
      return { vol, pct, ma };
    });
  },

  draw: (params: unknown): boolean => {
    const { ctx, visibleRange, bounding, xAxis, indicator, barSpace } =
      params as DrawParams;

    const results = indicator.result;
    const { from, to } = visibleRange;
    if (to <= from) return true;

    // Total horizontal slot per bar (body + gap)
    const cellW = barSpace
      ? barSpace.bar + barSpace.gapBar
      : Math.abs(xAxis.convertToPixel(from + 1) - xAxis.convertToPixel(from));
    const halfW = cellW / 2;

    // Leave 22 px at the top so the klinecharts pane legend is not obscured.
    const LEGEND_H = 22;
    const paneH = bounding.height - LEGEND_H;
    if (paneH <= 0) return true;

    ctx.save();
    for (let i = from; i <= to; i++) {
      const r = results[i];
      if (!r) continue;
      const x = xAxis.convertToPixel(i);
      ctx.fillStyle = spikeColor(r.pct);
      // +0.5 px closes sub-pixel gaps between adjacent cells
      ctx.fillRect(x - halfW, LEGEND_H, cellW + 0.5, paneH);
    }
    ctx.restore();

    return true; // suppress klinecharts' default line rendering
  },
};

export default volSpikeStrip;
